import { withZod } from '@remix-validated-form/with-zod'
import { WarningHexagon } from 'iconoir-react'
import type { ReactElement } from 'react'
import { useState } from 'react'
import { z } from 'zod'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import type { CdtCodeMappingType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { treatmentCategories } from 'lib/cdtCodeDefaults'
import { logError } from 'lib/logging'
import type { OptionType } from 'types/OptionType'

const cdtCodeMappingValidator = withZod(
  z.object({
    cdtCode: z.string().min(1, { message: `CDT Code is required` }),
    category: z.string().min(1, { message: `Category is required` }),
  }),
)

export function CdtCodeMappingForm({
  mapping,
  existingMappings,
  onSave,
  onDelete,
}: {
  mapping?: CdtCodeMappingType
  existingMappings: CdtCodeMappingType[]
  onSave(data: { cdtCode: string; category: string }, mappingId?: string): void
  onDelete(id: string): void
}): ReactElement {
  const [confirm] = useConfirmation()
  const [duplicateCategory, setDuplicateCategory] = useState<string | undefined>()
  const isEdit = !!mapping?.id
  const [selectedCategory, setSelectedCategory] = useState<OptionType | undefined>(
    mapping?.category ? { value: mapping.category, id: mapping.id!, name: mapping.category } : undefined,
  )

  const handleSubmit = async (data: Partial<CdtCodeMappingType>): Promise<void> => {
    setDuplicateCategory(undefined)
    const result = await cdtCodeMappingValidator.validate(data)
    if (result.error) {
      logError(`error saving CDT code mapping`, result.error)
      notifyError(`Error Saving`)
      return
    }
    const { cdtCode, category } = result.data
    const dupe = existingMappings.find(
      (m) => m.cdtCode?.toUpperCase() === cdtCode.toUpperCase() && m.id !== mapping?.id,
    )
    if (dupe) {
      setDuplicateCategory(dupe.category ?? `another category`)
      return
    }
    onSave({ cdtCode, category }, mapping?.id)
    closeModal()
    notifySuccess(mapping?.id ? `Change Saved. This may take some time to fully apply.` : `CDT Code Mapping Created`)
  }

  const handleDelete = async (id: string) => {
    onDelete(id)
    closeModal()
    notifySuccess(`Deleted Successfully`)
  }

  return (
    <SmartForm<Partial<CdtCodeMappingType>>
      id="cdt-code-mapping-form"
      validator={cdtCodeMappingValidator}
      className="mb-0 mt-0 flex w-[680px] flex-col gap-y-0"
      defaultValues={mapping}
      onSubmit={(data) => handleSubmit(data!)}
      hiddenInputs={[{ name: `id`, value: mapping?.id }]}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col px-6 py-4">
        <div className="flex items-end gap-x-2">
          <div className="flex-1">
            <FormInput
              labelClassName="text-strong text-body-sm font-medium"
              className="h-10 !px-2 !py-1"
              name="cdtCode"
              type="text"
              label="CDT Code"
            />
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5 mb-2.5 shrink-0"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.499 8.248h15m-15 7.501h15" />
          </svg>
          <div className="flex-1">
            <FormCombo
              label="Category"
              labelClassName="text-strong text-body-sm font-medium"
              inputClassName="!h-10 !px-2 !py-1 text-body-sm font-medium"
              name="category"
              placeholder="Select Category"
              value={selectedCategory}
              options={treatmentCategories.map((category) => ({
                id: category,
                value: category,
                name: category,
              }))}
              onChange={setSelectedCategory}
            />
          </div>
        </div>
      </div>
      <div className="border-t-trn col-span-6 flex w-full items-center justify-between border px-6 py-3">
        <Button
          mode="danger"
          size="sm"
          label="Delete"
          className={`mr-auto ${isEdit ? `` : `invisible`}`}
          onClick={async () => {
            await confirm({
              title: `Delete CDT code mapping`,
              description: `Are you sure you want to delete the CDT code mapping`,
              variant: `danger`,
            }).then(async () => handleDelete(mapping?.id!))
          }}
        />
        <div
          className={`${
            duplicateCategory ? `inline-flex` : `invisible`
          } w-full items-center justify-center gap-x-2 text-center text-red-700`}
        >
          <WarningHexagon className="size-4" />
          <span className="text-body-xs">This custom mapping already exists in {duplicateCategory}</span>
        </div>
        <Button mode="primary" size="sm" label={isEdit ? `Update` : `Create`} type="submit" className="text-center" />
      </div>
    </SmartForm>
  )
}
